import { useEffect, useState } from "react";
import {
  useFieldArray,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChevronDown, GripVertical, Plus, Settings2, X } from "lucide-react";
import isEqual from "lodash/isEqual";

import { Button } from "@/components/ui/button";
import {
  FormChipList,
  FormDatePicker,
  FormInput,
  FormMultiSelect,
  FormSelect,
  FormToggle,
} from "components/BaseForm";
import { capitalize } from "components/helpers";
import tagConstants from "constants/Tag";
import CategoricalTypeOptionsModal from "./CategoricalTypeOptionsModal";
import { cn } from "@/lib/utils";

const FIELD_ARRAY = "tag_metadata_fields";
const { metadataTypes } = tagConstants;

const TYPE_OPTIONS = Object.entries(metadataTypes).map(([key, value]) => ({
  key: value,
  value,
  text: capitalize(key),
}));

const isCategorical = type => type === metadataTypes.categorical;

const defaultForType = type => {
  switch (type) {
    case metadataTypes.boolean:
      return false;
    case metadataTypes.list:
      return [];
    case metadataTypes.categorical:
      return null;
    default:
      return "";
  }
};

const formatDefaultValue = value => {
  if (Array.isArray(value)) return value.length ? value.join(", ") : "None";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value == null || value === "") return "None";
  return String(value);
};

const newField = fieldType => ({
  field_name: "",
  field_type: fieldType,
  default_value: defaultForType(fieldType),
  allow_override: false,
  required: false,
  read_roles: [0],
  edit_roles: [0],
  field_options: {},
});

const DefaultValueControl = ({ index, fieldType, fieldOptions }) => {
  const name = `${FIELD_ARRAY}.${index}.default_value`;
  const label = "Default Value";
  switch (fieldType) {
    case metadataTypes.number:
      return <FormInput name={name} label={label} type="number" />;
    case metadataTypes.boolean:
      return <FormToggle name={name} label={label} />;
    case metadataTypes.date:
      return <FormDatePicker name={name} label={label} />;
    case metadataTypes.list:
      return <FormChipList name={name} label={label} placeholder="Add item…" />;
    case metadataTypes.categorical: {
      const options = (fieldOptions?.options ?? []).map(option => ({
        key: option,
        value: option,
        text: option,
      }));
      return fieldOptions?.type === "multi" ? (
        <FormMultiSelect name={name} label={label} options={options} />
      ) : (
        <FormSelect name={name} label={label} options={options} clearable />
      );
    }
    default:
      return <FormInput name={name} label={label} />;
  }
};

const MetadataFieldRow = ({
  id,
  index,
  userRoles,
  onRemove,
  onEditCategorical,
}) => {
  const { control, setValue } = useFormContext();
  const [open, setOpen] = useState(true);
  const row = useWatch({ control, name: `${FIELD_ARRAY}.${index}` });
  const fieldType = row?.field_type;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="tw:w-full tw:rounded-lg tw:border tw:border-[var(--color-border)] tw:bg-[var(--color-surface)]"
    >
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:p-3">
        <button
          type="button"
          className="tw:cursor-grab tw:text-muted-foreground tw:hover:text-foreground tw:active:cursor-grabbing"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="tw:size-4" />
        </button>
        <button
          type="button"
          onClick={() => setOpen(prev => !prev)}
          className="tw:flex tw:min-w-0 tw:flex-1 tw:cursor-pointer tw:items-center tw:gap-2 tw:text-left tw:text-sm tw:text-[var(--color-text-secondary)]"
        >
          <ChevronDown
            className={cn(
              "tw:max-h-4 tw:transition-transform",
              !open && "tw:-rotate-90"
            )}
          />
          <span className="tw:truncate">
            <strong className="tw:text-[var(--color-text-primary)]">
              Field Name:
            </strong>{" "}
            {row?.field_name || "New field"}
            {" - "}
            <strong className="tw:text-[var(--color-text-primary)]">
              Type:
            </strong>{" "}
            {capitalize(fieldType ?? "")}
            {" - "}
            <strong className="tw:text-[var(--color-text-primary)]">
              Default Value:
            </strong>{" "}
            {formatDefaultValue(row?.default_value)}
            {" - "}
            <strong className="tw:text-[var(--color-text-primary)]">
              Allow Override:
            </strong>{" "}
            {row?.allow_override ? "Yes" : "No"}
            {" - "}
            <strong className="tw:text-[var(--color-text-primary)]">
              Required:
            </strong>{" "}
            {row?.required ? "Yes" : "No"}
          </span>
        </button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Remove field"
          onClick={onRemove}
        >
          <X />
        </Button>
      </div>
      {open && (
        <div className="tw:grid tw:grid-cols-1 tw:gap-x-6 tw:gap-y-4 tw:p-3 tw:pt-0 tw:md:grid-cols-2">
          <FormInput
            name={`${FIELD_ARRAY}.${index}.field_name`}
            label="Field Name"
            required
          />
          <div className="tw:flex tw:items-end tw:gap-2">
            <div className="tw:flex-1">
              <FormSelect
                name={`${FIELD_ARRAY}.${index}.field_type`}
                label="Type"
                required
                options={TYPE_OPTIONS}
                onValueChange={next => {
                  setValue(
                    `${FIELD_ARRAY}.${index}.default_value`,
                    defaultForType(next)
                  );
                  setValue(`${FIELD_ARRAY}.${index}.field_options`, {});
                }}
              />
            </div>
            {isCategorical(fieldType) && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label="Edit categorical options"
                onClick={() => onEditCategorical(index)}
              >
                <Settings2 />
              </Button>
            )}
          </div>
          <DefaultValueControl
            index={index}
            fieldType={fieldType}
            fieldOptions={row?.field_options}
          />
          <div />
          <FormMultiSelect
            name={`${FIELD_ARRAY}.${index}.edit_roles`}
            label="View and Edit"
            options={userRoles}
            required
          />
          <FormMultiSelect
            name={`${FIELD_ARRAY}.${index}.read_roles`}
            label="View Only"
            options={userRoles}
            required
          />
          <FormToggle
            name={`${FIELD_ARRAY}.${index}.allow_override`}
            label="Allow Override"
          />
          <FormToggle
            name={`${FIELD_ARRAY}.${index}.required`}
            label="Required"
          />
        </div>
      )}
    </div>
  );
};

const TagMetadata = ({ userRoles, setConfirmationRequired, isEdit }) => {
  const { control, getValues, setValue } = useFormContext();
  const { defaultValues } = useFormState({ name: FIELD_ARRAY });
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: FIELD_ARRAY,
  });
  const [lastUsedType, setLastUsedType] = useState(metadataTypes.text);
  const [categorical, setCategorical] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );
  const rows = useWatch({ control, name: FIELD_ARRAY }) ?? [];
  const hasLockedDefaultChange = rows.some(row => {
    if (row.allow_override || row.id == null) return false;
    const originalField = defaultValues?.[FIELD_ARRAY]?.find(
      field => field.id === row.id
    );
    if (!originalField) return false;
    return !isEqual(originalField.default_value, row.default_value);
  });

  useEffect(() => {
    if (isEdit && hasLockedDefaultChange) {
      setConfirmationRequired?.(true);
    }
  }, [isEdit, hasLockedDefaultChange, setConfirmationRequired]);

  const handleAdd = () => {
    append(newField(lastUsedType));
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    move(
      fields.findIndex(f => f.id === active.id),
      fields.findIndex(f => f.id === over.id)
    );
  };

  const openCategorical = index => {
    setCategorical({
      index,
      fieldOptions: getValues(`${FIELD_ARRAY}.${index}.field_options`),
      defaultValue: getValues(`${FIELD_ARRAY}.${index}.default_value`),
    });
  };

  const saveCategorical = data => {
    setValue(`${FIELD_ARRAY}.${categorical.index}.field_options`, data.value);

    const { defaultValue } = categorical;
    const { type, options = [] } = data.value ?? {};
    const shapeChanged = categorical.fieldOptions?.type !== type;
    const stillValid =
      type === "multi"
        ? Array.isArray(defaultValue) &&
          defaultValue.every(v => options.includes(v))
        : defaultValue == null || options.includes(defaultValue);

    if (shapeChanged || !stillValid) {
      setValue(
        `${FIELD_ARRAY}.${categorical.index}.default_value`,
        type === "multi" ? [] : null
      );
    }
    setLastUsedType(metadataTypes.categorical);
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      {fields.length === 0 ? (
        <p className="tw:text-sm tw:text-[var(--color-text-muted)]">
          No metadata fields
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={fields.map(f => f.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="tw:flex tw:flex-col tw:gap-3">
              {fields.map((item, index) => (
                <MetadataFieldRow
                  key={item.id}
                  id={item.id}
                  index={index}
                  userRoles={userRoles}
                  onRemove={() => remove(index)}
                  onEditCategorical={openCategorical}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <div>
        <Button type="button" variant="outline" size="sm" onClick={handleAdd}>
          <Plus data-icon="inline-start" />
          Add Metadata Field
        </Button>
      </div>
      <CategoricalTypeOptionsModal
        open={categorical != null}
        onOpenChange={next => !next && setCategorical(null)}
        fieldOptions={categorical?.fieldOptions}
        onSave={saveCategorical}
      />
    </div>
  );
};

export default TagMetadata;
